import { useState, useCallback } from "react";
import { apiClient } from "../../utils/api";

interface AgentExecParams {
  agent_id: string;
  agent_kind: string;
  input_values: { [key: string]: any };
}

interface AgentExecResult {
  generated_text?: string;
  generated_images?: string[];
  shared_sid?: string;
}

interface UseAgentExecReturn {
  execute: (params: AgentExecParams) => Promise<AgentExecResult | null>;
  loading: boolean;
  error: string;
  clearError: () => void;
}

const useAgentExec = (): UseAgentExecReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const clearError = useCallback(() => setError(""), []);

  const execute = useCallback(
    async (params: AgentExecParams): Promise<AgentExecResult | null> => {
      setLoading(true);
      setError("");

      try {
        const res = await apiClient.post("/api/v1/agent/exec", params);

        if (res.data.status === false || res.data.error) {
          throw new Error(res.data.error || "서버에서 에러가 발생했습니다.");
        }

        return res.data;
      } catch (err: any) {
        console.error(err);
        let errorMessage = "알 수 없는 오류가 발생했습니다.";

        if (err.message) {
          if (err.message.includes("지원하지 않는")) {
            errorMessage =
              "현재 테스트 모드에서는 '문과생 맞춤 진로 탐색 테스트'만 지원됩니다.";
          } else if (
            err.message.includes("template:") &&
            err.message.includes("unexpected")
          ) {
            errorMessage =
              "에이전트 설정에 문제가 있습니다. 관리자에게 문의해주세요.";
          } else if (
            err.message.includes("connection reset by peer") ||
            err.message.includes("openai.com")
          ) {
            errorMessage =
              "AI 서비스 연결에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.";
          } else if (err.message.includes("timeout")) {
            errorMessage = "요청 시간이 초과되었습니다. 다시 시도해주세요.";
          } else if (err.message.includes("Network Error")) {
            errorMessage = "네트워크 연결을 확인하고 다시 시도해주세요.";
          } else {
            errorMessage = err.message;
          }
        }

        setError(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    execute,
    loading,
    error,
    clearError,
  };
};

export default useAgentExec;
export type { AgentExecParams, AgentExecResult, UseAgentExecReturn };
